"use client"

/**
 * 5-step "before you seed" prep checklist for /tools/seeding-calculator.
 * Replaces a wall of prose with a scannable card grid. Copy mirrors the
 * Claude Design polish for this tool.
 */

import type { ReactNode } from "react"

interface PrepStep {
  n: number
  title: string
  blurb: string
  icon: ReactNode
}

const PREP_STEPS: PrepStep[] = [
  {
    n: 1,
    title: "Test the soil",
    blurb:
      "Aim for pH 6.0–7.0 for most grasses. Cheap home test kit is fine; add lime if it’s low.",
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M9 3v8l-4 7a4 4 0 007 4 4 4 0 007-4l-4-7V3" />
        <path d="M9 3h6" />
      </svg>
    ),
  },
  {
    n: 2,
    title: "Clear and loosen",
    blurb:
      "Pull dead grass and weeds. Rake or scarify the top ¼ inch so seed touches soil, not thatch.",
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 21h18" />
        <path d="M7 21v-6M11 21v-9M15 21v-6M19 21v-9" />
        <path d="M5 15l14-6" />
      </svg>
    ),
  },
  {
    n: 3,
    title: "Spread at the right rate",
    blurb:
      "Split the seed in half: walk north–south, then east–west. Stops striping and patchiness.",
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="8" />
        <circle cx="8" cy="10" r="1" fill="currentColor" />
        <circle cx="14" cy="9" r="1" fill="currentColor" />
        <circle cx="11" cy="14" r="1" fill="currentColor" />
        <circle cx="16" cy="13" r="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    n: 4,
    title: "Top-dress lightly",
    blurb:
      "Cover with ¼ inch of peat or screened compost. Holds moisture so seed doesn’t cook on the surface.",
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 16h18" />
        <path d="M5 16c2-2 4-2 6 0s4 2 6 0 2 0 2 0" />
        <path d="M8 12v-2M12 11V8M16 12v-2" />
      </svg>
    ),
  },
  {
    n: 5,
    title: "Water 2–3× per day",
    blurb:
      "Keep the top inch damp for the first two weeks. Short bursts, not deep soaks, until germination.",
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 3s5 5 5 9a5 5 0 11-10 0c0-4 5-9 5-9z" />
        <path d="M9 14a3 3 0 003 3" />
      </svg>
    ),
  },
]

const CARPETGRASS_PREP_COPY: Record<number, Pick<PrepStep, "title" | "blurb">> = {
  1: {
    title: "Test the soil",
    blurb: "Test pH and nutrients before adding lime. Common carpetgrass grows in acidic soil; add amendments only when the test and local guidance support them.",
  },
  2: {
    title: "Clear and prepare",
    blurb: "Remove debris and competing plants. Correct poor drainage before planting.",
  },
  3: {
    title: "Follow the seed label",
    blurb: "Use the rate on the common carpetgrass seed label. This tool has no verified seed amount.",
  },
  4: {
    title: "Keep soil contact",
    blurb: "Prepare a clean, firm bed. Place seed or plugs as the planting material label directs.",
  },
  5: {
    title: "Keep it evenly moist",
    blurb: "Keep the planting area evenly moist without standing water. Use the label and site conditions to set irrigation.",
  },
}

export function SeedingPrepChecklist({ grassType }: { grassType?: string }) {
  const isCarpetgrass = grassType === "Carpetgrass"
  const steps = isCarpetgrass
    ? PREP_STEPS.map((step) => ({ ...step, ...CARPETGRASS_PREP_COPY[step.n] }))
    : PREP_STEPS
  return (
    <section aria-labelledby="seeding-prep-heading" className="text-gray-900">
      <h2 id="seeding-prep-heading" className="text-fluid-h2 font-bold tracking-tight text-gray-900">
        Before you seed: a 5-step prep checklist
      </h2>
      {isCarpetgrass && (
        <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-gray-700">
          For common carpetgrass, confirm drainage and follow the planting material label. This tool has no verified
          seed or watering rate.
        </p>
      )}
      <ol className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
        {steps.map((s) => (
          <li
            key={s.n}
            className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white px-5 pt-5 pb-4 shadow-elevation-2"
          >
            <div className="flex items-center gap-3">
              <span
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600"
                aria-hidden="true"
              >
                {s.icon}
              </span>
              <span className="text-[11px] font-bold uppercase tracking-wider text-green-700">Step {s.n}</span>
            </div>
            <div>
              <h3 className="mb-1 text-base font-semibold text-gray-900">{s.title}</h3>
              <p className="text-[13.5px] leading-normal text-gray-700">{s.blurb}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}
